import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { GiftOrder } from '../types';

interface CheckoutGuardProps {
  order: GiftOrder;
  children: React.ReactNode;
}

/*
 Centrally manages access to checkout steps based on the current order state.
 Prevents skipping steps or entering the funnel with incomplete data.
*/
export const CheckoutGuard: React.FC<CheckoutGuardProps> = ({ order, children }) => {
  const location = useLocation();
  const path = location.pathname;

  // Validation Flags
  const hasItems = order.items.length > 0;
  const hasVendor = order.vendor !== null;
  const hasSender = order.sender.fullName !== '' && order.sender.email !== '';
  const hasRecipient = order.recipient.fullName !== '' && order.recipient.phone !== '';
  const isSubscribe =
    order.isSubscribe || Boolean(order.subscription) || order.items.some((item) => Boolean(item.subscriptionOption));

  // Rule 1: Cannot go to Vendor selection without items
  if (path === ROUTES.VENDOR && !hasItems) {
    return <Navigate to={isSubscribe ? ROUTES.SUBSCRIBE : ROUTES.ITEMS} replace />;
  }

  // Rule 2: Cannot go to Sender info without items AND vendor
  if (path === ROUTES.SENDER && (!hasItems || !hasVendor)) {
    return <Navigate to={ROUTES.VENDOR} replace />;
  }

  // Rule 3: Cannot go to Recipient info without sender, items, vendor
  if (path === ROUTES.RECIPIENT && (!hasItems || !hasVendor || !hasSender)) {
    return <Navigate to={ROUTES.SENDER} replace />;
  }

  // Rule 4: Cannot go to Payment without recipient info, sender, vendor, items
  if (path === ROUTES.PAYMENT) {
    if (isSubscribe) {
      if (!hasItems || !hasVendor) {
        return <Navigate to={ROUTES.VENDOR} replace />;
      }
    } else if (!hasItems || !hasVendor || !hasSender || !hasRecipient) {
      return <Navigate to={ROUTES.RECIPIENT} replace />;
    }
  }

  // Rule 5: Cannot go to confirmation without items or vendor (likely session expired)
  if (path === ROUTES.CONFIRMATION && (!hasItems || !hasVendor)) {
    return <Navigate to={ROUTES.LANDING} replace />;
  }

  return <>{children}</>;
};
